import type { WorkspacePrefillRequestV1 } from '@/cms/components/blocks/workspace/workspace-prefill-contract';
import {
  resolveWorkspaceBatchRequiredCredits,
  resolveWorkspaceRequiredCredits,
} from '../actions/runtime-utils';
import type {
  WorkspaceToolParamValue,
  WorkspaceToolView,
} from '../contracts/tool-view';
import {
  type WorkspacePrefillFormModel,
  type WorkspacePrefillValidationIssue,
  buildWorkspacePrefillFormModel,
  validateWorkspacePrefillRequest,
} from './workspace-prefill-model';

export type WorkspacePrefillApplication = {
  prompt?: string;
  primaryFiles: File[];
  referenceFilesByFieldId: Record<string, File[]>;
  toolParams: Record<string, WorkspaceToolParamValue>;
};

export type WorkspacePrefillApplicationResult =
  | { ok: true; application: WorkspacePrefillApplication }
  | { ok: false; issues: WorkspacePrefillValidationIssue[] };

export function buildWorkspacePrefillApplication(params: {
  form: WorkspacePrefillFormModel;
  request: WorkspacePrefillRequestV1;
}): WorkspacePrefillApplication {
  const application: WorkspacePrefillApplication = {
    primaryFiles: [],
    referenceFilesByFieldId: {},
    toolParams: {},
  };

  for (const field of params.form.fields) {
    const value =
      params.request.valuesByFieldId[field.id] ?? field.defaultValue;
    const files = params.request.filesByFieldId[field.id] ?? [];

    if (field.inputRole === 'prompt') {
      if (value !== undefined) {
        if (typeof value !== 'string') {
          throw new Error(
            `Workspace prefill prompt field ${field.id} must resolve to text.`
          );
        }
        application.prompt = value;
      }
      continue;
    }

    if (field.inputRole === 'primaryImage') {
      application.primaryFiles.push(...files);
      continue;
    }

    if (field.inputRole === 'referenceImage') {
      if (files.length > 0) {
        application.referenceFilesByFieldId[field.id] = files;
      }
      continue;
    }

    if (files.length > 0) {
      throw new Error(
        `Workspace prefill file field ${field.id} requires an image input role.`
      );
    }
    if (value !== undefined) {
      application.toolParams[field.id] = value;
    }
  }

  return application;
}

function buildPrefillCreditFileToken(fieldId: string, index: number) {
  return `workspace-prefill://${fieldId}/${index + 1}`;
}

export function calculateWorkspacePrefillRequiredCredits(params: {
  form: WorkspacePrefillFormModel;
  request: WorkspacePrefillRequestV1;
}) {
  const application = buildWorkspacePrefillApplication(params);
  const referenceImageUrlsByFieldId = Object.fromEntries(
    Object.entries(application.referenceFilesByFieldId).map(
      ([fieldId, files]) => [
        fieldId,
        files.map((_, index) => buildPrefillCreditFileToken(fieldId, index)),
      ]
    )
  );

  if (application.primaryFiles.length > 1) {
    return resolveWorkspaceBatchRequiredCredits({
      tool: params.form.creditQuoteTool,
      toolParams: application.toolParams,
      items: application.primaryFiles.map((_, index) => ({
        inputImageUrl: buildPrefillCreditFileToken('primaryImage', index),
      })),
      prompt: application.prompt,
      quotedCredits: null,
    });
  }

  return resolveWorkspaceRequiredCredits({
    tool: params.form.creditQuoteTool,
    toolParams: application.toolParams,
    inputImageUrl:
      application.primaryFiles.length > 0
        ? buildPrefillCreditFileToken('primaryImage', 0)
        : null,
    prompt: application.prompt,
    referenceImageUrlsByFieldId,
    quotedCredits: null,
  });
}

export function resolveWorkspacePrefillApplication(params: {
  tool: WorkspaceToolView;
  request: WorkspacePrefillRequestV1;
}): WorkspacePrefillApplicationResult {
  const form = buildWorkspacePrefillFormModel(params.tool);
  const validation = validateWorkspacePrefillRequest(form, params.request);
  if (!validation.ok) {
    return validation;
  }

  return {
    ok: true,
    application: buildWorkspacePrefillApplication({
      form,
      request: validation.value,
    }),
  };
}
